import { __decorate, __metadata } from "tslib";
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserManagementApiService } from './user-management-api.service';
import { CommissionTypesService, EmailStatusService, IntegrationStatusService, UserIntegrationStatusService, UserLeadStatusLabelsService, UserStatus, UserStatusService, } from '@ho/shared/data-access/dictionary';
import { map, pluck } from 'rxjs/operators';
import { map as _map } from 'lodash-es';
import { SessionUserTypeService, UserType } from '@ho/shared/data-access/session';
let UserManagementService = class UserManagementService {
    constructor(_userManagementApiService, _sessionUserTypeService, _userLeadStatusLabelsService, _userStatusService, _emailStatusService, _integrationStatusService, _translate, _userIntegrationStatusService, _commissionTypesService) {
        this._userManagementApiService = _userManagementApiService;
        this._sessionUserTypeService = _sessionUserTypeService;
        this._userLeadStatusLabelsService = _userLeadStatusLabelsService;
        this._userStatusService = _userStatusService;
        this._emailStatusService = _emailStatusService;
        this._integrationStatusService = _integrationStatusService;
        this._translate = _translate;
        this._userIntegrationStatusService = _userIntegrationStatusService;
        this._commissionTypesService = _commissionTypesService;
    }
    affiliates(body) {
        return this._userManagementApiService.affiliates(body).pipe(map((response) => {
            response.body['data'] = _map(response.body['data'], (affiliateRequest) => {
                return Object.assign(Object.assign({}, affiliateRequest), { isBanned: affiliateRequest.status_id === UserStatus.Inactive, postbacksLink: `/d/user-operations/affiliates/${affiliateRequest.id}/postbacks`, isLink: this._sessionUserTypeService.isAdminRole() ||
                        affiliateRequest.invited_by_workspace_role_id === UserType.Manager, status_label: this._userIntegrationStatusService.get(affiliateRequest.integration_status), statusClass: this._userIntegrationStatusService.getStyleClass(affiliateRequest.integration_status), status_id_label: this._userStatusService.get(affiliateRequest.status_id), statusIdClass: this._userStatusService.getStyleClass(affiliateRequest.status_id), commission_type_name: this._commissionTypesService.getName(affiliateRequest.commission_type_id) });
            });
            return response;
        }));
    }
    managers(body) {
        return this._userManagementApiService.managers(body).pipe(map((response) => {
            response.body['data'] = _map(response.body['data'], (managerRequest) => {
                return Object.assign(Object.assign({}, managerRequest), { isBanned: managerRequest.status_id === UserStatus.Inactive, status_id_label: this._userStatusService.get(managerRequest.status_id), statusIdClass: this._userStatusService.getStyleClass(managerRequest.status_id) });
            });
            return response;
        }));
    }
    userAccounts(body) {
        return this._userManagementApiService.userAccounts(body).pipe(map((response) => {
            response.body['data'] = _map(response.body['data'], (account) => {
                return this._prepareAccountData(account);
            });
            return response;
        }));
    }
    affiliateRequests(body) {
        return this._userManagementApiService.affiliateRequests(body).pipe(map((response) => {
            response.body['data'] = _map(response.body['data'], (userRequest) => {
                return Object.assign(Object.assign({}, userRequest), { leadStatusClass: this._userLeadStatusLabelsService.getStyleClass(userRequest.lead_status), leadStatusName: this._userLeadStatusLabelsService.get(userRequest.lead_status), isLink: this._sessionUserTypeService.isAdminRole() ||
                        userRequest.invited_by_workspace_role_id === UserType.Manager });
            });
            return response;
        }));
    }
    affiliateRegistrationStats(body) {
        return this._userManagementApiService.affiliateRegistrationStats(body).pipe(map((response) => {
            response.body['data'] = _map(response.body['data'], (stats) => {
                return Object.assign(Object.assign({}, stats), { isEmailConfirmedStatusClass: this._emailStatusService.getStyleClass(stats.is_email_confirmed), integrationStatusClass: this._integrationStatusService.getStyleClass(stats.integration_status), integrationStatusName: this._integrationStatusService.get(stats.integration_status), leadStatusClass: this._userLeadStatusLabelsService.getStyleClass(stats.lead_status), leadStatusName: this._userLeadStatusLabelsService.get(stats.lead_status), systemStatusClass: this._userStatusService.getStyleClass(stats.status), systemStatusName: this._userStatusService.get(stats.status), emailStatus: this._emailStatusService.get(stats.is_email_confirmed), isLinkInvitedBy: this._sessionUserTypeService.isAdminRole() ||
                        stats.invited_by_workspace_role_id === UserType.Manager, isLinkAffiliateManager: this._sessionUserTypeService.isAdminRole() ||
                        stats.affiliate_manager_workspace_role_id === UserType.Manager });
            });
            return response;
        }));
    }
    exportRegistrationStatsCsv(params) {
        return this._userManagementApiService.exportRegistrationStatsCsv(params);
    }
    emailVerification(body) {
        return this._userManagementApiService.emailVerification(body);
    }
    getAffiliateRequest(id) {
        return this._userManagementApiService.getAffiliateRequest(id).pipe(pluck('data'));
    }
    batchApproveRequests(body) {
        return this._userManagementApiService.batchApproveRequests(body).pipe(pluck('data'));
    }
    batchDeclineRequests(body) {
        return this._userManagementApiService.batchDeclineRequests(body).pipe(pluck('data'));
    }
    batchSetLeadStatus(body, type) {
        return this._userManagementApiService.batchSetStatus(body, type).pipe(pluck('data'));
    }
    batchSetCommissionType(body) {
        return this._userManagementApiService.batchSetCommissionType(body).pipe(pluck('data'));
    }
    commentAccount(body, id) {
        return this._userManagementApiService.commentAccount(body, id).pipe(pluck('data'));
    }
    commentRequest(body, id) {
        return this._userManagementApiService.commentRequest(body, id).pipe(pluck('data'));
    }
    getAccount(id) {
        return this._userManagementApiService
            .getAccount(id)
            .pipe(map((account) => this._prepareAccountData(account)));
    }
    getAccountRole(id) {
        return this._userManagementApiService.getAccountRole(id).pipe(pluck('data'));
    }
    banAccount(id, body) {
        return this._userManagementApiService.banAccount(id, body);
    }
    unbanAccount(id, body) {
        return this._userManagementApiService.unbanAccount(id, body);
    }
    patchAffiliateProfile(id, body) {
        return this._userManagementApiService.updateAccount(id, body);
    }
    updateAccount(id, body) {
        return this._userManagementApiService.updateAccount(id, body).pipe(pluck('data'));
    }
    changeAccountPassword(id, body) {
        return this._userManagementApiService.changeAccountPassword(id, body);
    }
    userChangeLog(body) {
        return this._userManagementApiService.userChangeLog(body);
    }
    exportCsv(params) {
        return this._userManagementApiService.exportCsv(params);
    }
    registrationLogExportPdf(params) {
        return this._userManagementApiService.registrationLogExportPdf(params);
    }
    setTrafficChannel(id, body) {
        return this._userManagementApiService.setTrafficChannel(id, body).pipe(pluck('data'));
    }
    createAffiliate(body) {
        return this._userManagementApiService.createAffiliate(body);
    }
    createManager(body) {
        return this._userManagementApiService.createManager(body);
    }
    createAD(body) {
        return this._userManagementApiService.createAD(body);
    }
    createAdmin(body) {
        return this._userManagementApiService.createAdmin(body);
    }
    batchChangeManager(body) {
        return this._userManagementApiService.batchChangeManager(body).pipe(pluck('data'));
    }
    changeSecretKey(id, body) {
        return this._userManagementApiService.changeSecretKey(id, body).pipe(pluck('data'));
    }
    _prepareAccountData(account) {
        var _a, _b, _c, _d;
        return Object.assign(Object.assign({}, account), { username: account.username, registered_by_id: (_a = account.registered_by) === null || _a === void 0 ? void 0 : _a.id, registered_by_name: (_b = account.registered_by) === null || _b === void 0 ? void 0 : _b.username, manager_id: account.manager_id || ((_c = account.manager) === null || _c === void 0 ? void 0 : _c.id), manager_label: account.manager_label || ((_d = account.manager) === null || _d === void 0 ? void 0 : _d.username), status_id_label: this._userStatusService.get(account.status_id || account.status) || account.status, statusIdClass: this._userStatusService.getBadgeClass(account.status_id || account.status || UserStatus.Active), account_status: account.status, mobile: account.mobile });
    }
};
UserManagementService.ctorParameters = () => [
    { type: UserManagementApiService },
    { type: SessionUserTypeService },
    { type: UserLeadStatusLabelsService },
    { type: UserStatusService },
    { type: EmailStatusService },
    { type: IntegrationStatusService },
    { type: TranslateService },
    { type: UserIntegrationStatusService },
    { type: CommissionTypesService }
];
UserManagementService = __decorate([
    Injectable({
        providedIn: 'root',
    }),
    __metadata("design:paramtypes", [UserManagementApiService,
        SessionUserTypeService,
        UserLeadStatusLabelsService,
        UserStatusService,
        EmailStatusService,
        IntegrationStatusService,
        TranslateService,
        UserIntegrationStatusService,
        CommissionTypesService])
], UserManagementService);
export { UserManagementService };
